import { createSelector, Selector } from '@ngxs/store';
import { SettingsStateModel } from '@store/models/settings.model';
import { SettingsState } from '@store/states/settings.state';
import { PeriodItemI, PeriodsI } from '@models/periods';
import { settingsToCompletePeriods, toetsInplanPeriod } from '@store/utils/settings-periods.util';

export class SettingsSelectors {
  @Selector([SettingsState])
  static settings(state: SettingsStateModel) {
    return state.settings;
  }

  /**
   * Derived selector: automatically recalculates periods whenever settings change.
   * This ensures withinRange booleans are always up-to-date.
   * Returns undefined until settings have been loaded from the API.
   */
  @Selector([SettingsState])
  static settingsPeriods(state: SettingsStateModel): PeriodsI | undefined {
    if (!state?.settings || !state.isLoaded) {
      return undefined;
    }
    return settingsToCompletePeriods(state.settings);
  }

  /**
   * Inplanperiode of a single toets (e.g. 'DOEPRT-D'), derived from its toets settings.
   * Returns undefined until settings have been loaded from the API.
   */
  static toetsInplanPeriod(toetsBusinessKey: string) {
    return createSelector([SettingsState], (state: SettingsStateModel): PeriodItemI | undefined => {
      if (!state?.settings || !state.isLoaded) {
        return undefined;
      }
      return toetsInplanPeriod(state.settings, toetsBusinessKey);
    });
  }

  @Selector([SettingsState])
  static specificSettings(state: SettingsStateModel) {
    return state.settings.toetsSettings?.specifiekeToetsSettings || [];
  }

  @Selector([SettingsState])
  static isSettingsLoading(state: SettingsStateModel) {
    return state.isLoading;
  }

  @Selector([SettingsState])
  static settingsError(state: SettingsStateModel) {
    return state.error;
  }

  @Selector([SettingsState])
  static isSettingsLoaded(state: SettingsStateModel) {
    return state.isLoaded;
  }

  @Selector([SettingsState])
  static isSettingsSaved(state: SettingsStateModel) {
    return state.isSaved;
  }
}
